import React from 'react';
import { Group, Circle, RegularPolygon, Rect, Ellipse, Line } from 'react-konva';
import { IconStyle, ThemePalette } from '../../types';
import { NODE_TYPES } from '../../constants/nodeTypes';

interface NodeIconProps {
  type: string;
  accentColor: string;
  x: number;
  y: number;
  iconStyle?: IconStyle;
  palette?: ThemePalette;
}

/** Get canonical type color */
function getTypeColor(type: string): string {
  return (NODE_TYPES as Record<string, { color: string }>)[type]?.color ?? '#6366f1';
}

/** Darken a hex color ~30% for the badge background */
function darken(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.floor(r * 0.65)},${Math.floor(g * 0.65)},${Math.floor(b * 0.65)})`;
}

/** White icon shape drawn at local (0,0), ~10-14px scale */
function IconShape({ type, color }: { type: string; color: string }) {
  const W = { fill: color, listening: false, perfectDrawEnabled: false } as const;

  switch (type) {
    case 'service':
      // Gear with protruding teeth
      return (
        <Group>
          {/* Outer circle (base of teeth) */}
          <Circle radius={6} stroke={color} strokeWidth={1.5} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          {/* 8 teeth protruding outward */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            const x1 = Math.cos(rad) * 6;
            const y1 = Math.sin(rad) * 6;
            const x2 = Math.cos(rad) * 9;
            const y2 = Math.sin(rad) * 9;
            return (
              <Line key={angle} points={[x1, y1, x2, y2]} stroke={color} strokeWidth={2}
                listening={false} perfectDrawEnabled={false} />
            );
          })}
          {/* Inner hub circle */}
          <Circle radius={3} stroke={color} strokeWidth={1} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          {/* Center hole */}
          <Circle radius={1} fill={color} listening={false} perfectDrawEnabled={false} />
        </Group>
      );

    case 'database':
    case 'cylinder':
      // Improved cylinder with visible rim
      return (
        <Group>
          {/* Body */}
          <Rect x={-6} y={-3} width={12} height={7} fill={color} listening={false} perfectDrawEnabled={false} />
          {/* Top ellipse (rim) */}
          <Ellipse radiusX={6} radiusY={3} y={-3} fill={color} listening={false} perfectDrawEnabled={false} />
          {/* Rim highlight line */}
          <Ellipse radiusX={5} radiusY={2.2} y={-3}
            stroke={color === 'white' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)'}
            strokeWidth={0.8} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          {/* Bottom ellipse */}
          <Ellipse radiusX={6} radiusY={3} y={4} fill={color} listening={false} perfectDrawEnabled={false} />
          {/* Middle line for stacked-disk look */}
          <Ellipse radiusX={5.5} radiusY={1.8} y={0.5}
            stroke={color === 'white' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.35)'}
            strokeWidth={0.6} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
        </Group>
      );

    case 'api':
      // Lightning bolt — two triangular segments
      return (
        <Group>
          <Line points={[2, -7, -3, 0, 1, 0]} closed stroke={color} strokeWidth={0.5} {...W} />
          <Line points={[-1, 0, 3, 0, -2, 7]} closed stroke={color} strokeWidth={0.5} {...W} />
        </Group>
      );

    case 'person':
      // Circle head + ellipse shoulders
      return (
        <Group>
          <Circle radius={3.5} y={-5} {...W} />
          <Ellipse radiusX={6} radiusY={4} y={2} {...W} />
        </Group>
      );

    case 'queue':
      // Three horizontal stacked bars
      return (
        <Group>
          <Rect x={-6} y={-5} width={12} height={2.5} cornerRadius={1} {...W} />
          <Rect x={-6} y={-1} width={12} height={2.5} cornerRadius={1} {...W} />
          <Rect x={-6} y={3} width={12} height={2.5} cornerRadius={1} {...W} />
        </Group>
      );

    case 'system':
      // Monitor: screen rect + stand + base
      return (
        <Group>
          <Rect x={-7} y={-6} width={14} height={9} cornerRadius={1} {...W} />
          <Rect x={-1.5} y={3} width={3} height={3} {...W} />
          <Rect x={-5} y={6} width={10} height={2} cornerRadius={1} {...W} />
        </Group>
      );

    case 'group':
      // Square brackets [ ]
      return (
        <Group>
          <Line
            points={[-2.5, -7, -7, -7, -7, 7, -2.5, 7]}
            stroke={color} strokeWidth={2}
            listening={false} perfectDrawEnabled={false}
          />
          <Line
            points={[2.5, -7, 7, -7, 7, 7, 2.5, 7]}
            stroke={color} strokeWidth={2}
            listening={false} perfectDrawEnabled={false}
          />
        </Group>
      );

    case 'decision':
      // Two arrows staggered — left and right branching
      return (
        <Group>
          {/* Left arrow (top) */}
          <Line points={[-2, -3, -6, -3, -4, -5]} stroke={color} strokeWidth={1.5} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          <Line points={[-2, -3, -6, -3, -4, -1]} stroke={color} strokeWidth={1.5} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          <Line points={[-6, -3, 2, -3]} stroke={color} strokeWidth={1.5}
            listening={false} perfectDrawEnabled={false} />
          {/* Right arrow (bottom) */}
          <Line points={[2, 3, 6, 3, 4, 1]} stroke={color} strokeWidth={1.5} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          <Line points={[2, 3, 6, 3, 4, 5]} stroke={color} strokeWidth={1.5} fill="transparent"
            listening={false} perfectDrawEnabled={false} />
          <Line points={[6, 3, -2, 3]} stroke={color} strokeWidth={1.5}
            listening={false} perfectDrawEnabled={false} />
        </Group>
      );

    case 'external':
      // Dashed rectangle outline
      return (
        <Rect
          x={-7} y={-6} width={14} height={12}
          stroke={color} strokeWidth={2} dash={[3, 2]}
          fill="transparent"
          listening={false} perfectDrawEnabled={false}
        />
      );

    case 'logic':
      // Brain icon for logic nodes
      return <RegularPolygon sides={6} radius={6} {...W} />;

    default:
      return <RegularPolygon sides={6} radius={6} {...W} />;
  }
}

export const NodeIcon: React.FC<NodeIconProps> = ({ type, x, y, iconStyle = 'badge' }) => {
  const typeColor = getTypeColor(type);

  if (iconStyle === 'solid') {
    // No badge circle — just the icon, slightly larger, type-colored
    return (
      <Group x={x} y={y} listening={false} scaleX={1.3} scaleY={1.3}>
        <IconShape type={type} color={typeColor} />
      </Group>
    );
  }

  // Badge style (default) — badge circle uses type color
  return (
    <Group x={x} y={y} listening={false}>
      {/* Badge circle */}
      <Circle
        radius={14}
        fill={darken(typeColor)}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth={1.5}
        perfectDrawEnabled={false}
        listening={false}
      />
      {/* Type-specific icon */}
      <IconShape type={type} color="white" />
    </Group>
  );
};
